import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
  Select,
  MenuItem,
  CircularProgress,
} from "@material-ui/core";
import { CalendarToday } from "@material-ui/icons";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CameraIcon from "../../assets/images/mynaui_camera-solid.svg";
import chooseUserTypeStyle from "../styles/chooseUserTypeStyle";
import complatedProfileStyle from "../styles/complateProfileStyle";
import { ProfileData, FormErrors } from "../types";
import { COUNTRIES } from "../../config/countries";
import { countryToFlag } from "../../utils/countryDetection";
import { SUBDIVISIONS, getCountryCode } from "../utils/locationUtils";

interface ViewerProfileStepProps {
  isMobile: boolean;
  profileData: ProfileData;
  viewerErrors: FormErrors;
  handleFieldChange: (field: any, value: any) => void;
  handleFieldBlur: (field: any, value: any) => void;
  isCheckingUsername: boolean;
  usernameAvailable: boolean | null;
  setUploadMediaModalOpen: (open: boolean) => void;
  prevStep: () => void;
  isFormValid: boolean;
  handleProfileChange: () => void;
  isSubmitting?: boolean;
}

const ViewerProfileStep: React.FC<ViewerProfileStepProps> = ({
  isMobile,
  profileData,
  viewerErrors,
  handleFieldChange,
  handleFieldBlur,
  isCheckingUsername,
  usernameAvailable,
  setUploadMediaModalOpen,
  prevStep,
  isFormValid,
  handleProfileChange,
  isSubmitting,
}) => {
  const classes = chooseUserTypeStyle();
  const profileStyle = complatedProfileStyle();
  const creatorClasses = chooseUserTypeStyle({ selected: false });

  const countryCode = profileData.country ? getCountryCode(profileData.country) : null;
  const hasSubdivisions = countryCode && SUBDIVISIONS[countryCode];

  return (
    <>
      <Typography variant="h4" className={profileStyle.title}>
        Complete Your Profile
      </Typography>
      <Typography
        style={{
          display: "block",
          width: "100%",
          color: "#FFFFFF",
          fontSize: isMobile ? "12px" : "16px",
          fontWeight: isMobile ? 400 : 500,
          marginBottom: "16px",
          textAlign: isMobile ? "center" : "left",
        }}
      >
        Add information to get started and create a personalized experience
      </Typography>
      <Paper className={profileStyle.formCard}>
        <div>
          {/* Upload Profile Image Button */}
          <Box
            onClick={() => setUploadMediaModalOpen(true)}
            style={{
              cursor: "pointer",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            {/* Avatar Circle */}
            <Box
              style={{
                width: isMobile ? "68px" : "120px",
                height: isMobile ? "68px" : "120px",
                borderRadius: "100%",
                backgroundColor: "#6C6C6C",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                position: "relative",
                overflow: "hidden",
              }}
            >
              {profileData.profileImg ? (
                <img
                  src={
                    profileData.profileImg instanceof File
                      ? URL.createObjectURL(profileData.profileImg)
                      : profileData.profileImg
                  }
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <img
                  src={CameraIcon}
                  alt="Camera"
                  style={{
                    width: "40px",
                    height: "40px",
                    opacity: 0.5,
                  }}
                />
              )}
            </Box>

            {/* Text Content */}
            <Box style={{ flex: 1 }}>
              <Typography
                variant="body2"
                style={{
                  fontSize: isMobile ? "16px" : "20px",
                  fontWeight: isMobile ? 700 : 600,
                  color: "#FFFFFF",
                  marginBottom: "4px",
                }}
              >
                Upload profile image
              </Typography>
              <Typography
                variant="body2"
                style={{
                  fontSize: isMobile ? "12px" : "20px",
                  fontWeight: 600,
                  color: "#FE5A97",
                }}
              >
                Click to upload
              </Typography>
            </Box>
          </Box>
          {viewerErrors.profileImg && (
            <Typography
              variant="caption"
              style={{
                color: "#f44336",
                fontSize: "12px",
                marginTop: "4px",
              }}
            >
              {viewerErrors.profileImg}
            </Typography>
          )}

          <Box style={{ marginTop: "24px" }}>
            {/* Row 1: Name*, Surname* */}
            <Box style={{ display: "flex", gap: "24px" }}>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel shrink className={classes.customLabel}>
                  Name*
                </InputLabel>
                <Input
                  placeholder="Enter First Name"
                  value={profileData.firstName}
                  className={classes.customInput}
                  onChange={e => handleFieldChange("firstName", e.target.value)}
                  onBlur={e => handleFieldBlur("firstName", e.target.value)}
                  error={!!viewerErrors.firstName}
                />
                {viewerErrors.firstName && (
                  <Typography
                    variant="caption"
                    style={{
                      color: "#f44336",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {viewerErrors.firstName}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth className={classes.formControl}>
                <InputLabel shrink className={classes.customLabel}>
                  Surname*
                </InputLabel>
                <Input
                  placeholder="Enter Last Name"
                  value={profileData.lastName}
                  className={classes.customInput}
                  onChange={e => handleFieldChange("lastName", e.target.value)}
                  onBlur={e => handleFieldBlur("lastName", e.target.value)}
                  error={!!viewerErrors.lastName}
                />
                {viewerErrors.lastName && (
                  <Typography
                    variant="caption"
                    style={{
                      color: "#f44336",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {viewerErrors.lastName}
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* Row 2: Username, Birth date* */}
            <Box style={{ display: "flex", gap: "24px", marginTop: "20px" }}>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel shrink className={classes.customLabel}>
                  Username
                </InputLabel>
                <Input
                  placeholder="Enter Username"
                  value={profileData.username}
                  className={classes.customInput}
                  onChange={e => handleFieldChange("username", e.target.value)}
                  onBlur={e => handleFieldBlur("username", e.target.value)}
                  error={!!viewerErrors.username}
                  endAdornment={
                    profileData.username.trim().length >= 3 && (
                      <InputAdornment position="end">
                        {isCheckingUsername ? (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#A3A3A3",
                              fontSize: "12px",
                            }}
                          >
                            Checking...
                          </Typography>
                        ) : usernameAvailable === true ? (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#4caf50",
                              fontSize: "12px",
                            }}
                          >
                            Available
                          </Typography>
                        ) : usernameAvailable === false ? (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#f44336",
                              fontSize: "12px",
                            }}
                          >
                            Taken
                          </Typography>
                        ) : null}
                      </InputAdornment>
                    )
                  }
                />
                {viewerErrors.username && (
                  <Typography
                    variant="caption"
                    style={{
                      color: "#f44336",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {viewerErrors.username}
                  </Typography>
                )}
                {!viewerErrors.username &&
                  profileData.username.trim().length >= 3 &&
                  usernameAvailable === true && (
                    <Typography
                      variant="caption"
                      style={{
                        color: "#4caf50",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      Username is available
                    </Typography>
                  )}
              </FormControl>

              <FormControl fullWidth className={classes.formControl}>
                <InputLabel
                  shrink
                  className={classes.customLabel}
                  style={{ color: "#FFFFFF" }}
                >
                  Birth date*
                </InputLabel>
                <Input
                  type="date"
                  value={profileData.DOB}
                  className={classes.customInput}
                  startAdornment={
                    <InputAdornment position="start">
                      <CalendarToday style={{ fontSize: 18 }} />
                    </InputAdornment>
                  }
                  onChange={e => handleFieldChange("DOB", e.target.value)}
                  onBlur={e => handleFieldBlur("DOB", e.target.value)}
                  error={!!viewerErrors.DOB}
                  inputProps={{
                    style: {
                      cursor: "pointer",
                      color: profileData.DOB ? "#FFFFFF" : "#FFFFFF",
                    },
                    placeholder: "Select your birth date",
                    onClick: (e: any) => {
                      e.target.showPicker?.();
                    },
                  }}
                />
                {viewerErrors.DOB && (
                  <Typography
                    variant="caption"
                    style={{
                      color: "#f44336",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    {viewerErrors.DOB}
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* Row 3: Country, State/Province (if applicable), City (Input) */}
            <Box style={{ display: "flex", gap: "24px", marginTop: "20px" }}>
              <FormControl fullWidth className={classes.formControl}>
                <InputLabel shrink className={classes.customLabel}>
                  Country
                </InputLabel>
                <Select
                  value={profileData.country || ""}
                  onChange={e => {
                    const newCountry = e.target.value as string;
                    handleFieldChange("country", newCountry);
                    handleFieldChange("subdivision", "");
                    handleFieldChange("city", "");
                  }}
                  displayEmpty
                  IconComponent={ArrowDropDownIcon}
                  className={classes.customInput}
                  MenuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                >
                  {[
                    "United States",
                    "United Kingdom",
                    "Canada",
                    "Australia",
                  ].map(c => (
                    <MenuItem key={c} value={c} style={{ color: "#000" }}>
                      <span style={{ marginRight: 8 }}>{countryToFlag(c)}</span>
                      {c}
                    </MenuItem>
                  ))}
                  <MenuItem disabled value="__sep__" style={{ color: "#999" }}>
                    ———————————————
                  </MenuItem>
                  {COUNTRIES.filter(
                    c =>
                      ![
                        "United States",
                        "United Kingdom",
                        "Canada",
                        "Australia",
                      ].includes(c)
                  ).map(c => (
                    <MenuItem key={c} value={c} style={{ color: "#000" }}>
                      <span style={{ marginRight: 8 }}>{countryToFlag(c)}</span>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {hasSubdivisions ? (
                <FormControl fullWidth className={classes.formControl}>
                  <InputLabel shrink className={classes.customLabel}>
                    State/Province
                  </InputLabel>
                  <Select
                    value={profileData.subdivision || ""}
                    onChange={e => handleFieldChange("subdivision", e.target.value)}
                    displayEmpty
                    IconComponent={ArrowDropDownIcon}
                    className={classes.customInput}
                    MenuProps={{
                      getContentAnchorEl: null,
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          backgroundColor: "#fff",
                        },
                      },
                    }}
                  >
                    <MenuItem value="" style={{ color: "#000" }}>
                      <em>Select State/Province</em>
                    </MenuItem>
                    {SUBDIVISIONS[countryCode!].map(subdivision => (
                      <MenuItem
                        key={subdivision}
                        value={subdivision}
                        style={{ color: "#000" }}
                      >
                        {subdivision}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <FormControl fullWidth className={classes.formControl}>
                  <InputLabel shrink className={classes.customLabel}>
                    City
                  </InputLabel>
                  <Input
                    value={profileData.city || ""}
                    onChange={e => handleFieldChange("city", e.target.value)}
                    className={classes.customInput}
                    placeholder="Enter city"
                  />
                </FormControl>
              )}
            </Box>

            {/* City Input - shown below when subdivisions exist */}
            {hasSubdivisions && (
              <Box style={{ marginTop: "20px", marginRight: 20, display: "flex" }}>
                <FormControl className={classes.formControl} style={{ width: "50%" }}>
                  <InputLabel shrink className={classes.customLabel}>
                    City
                  </InputLabel>
                  <Input
                    value={profileData.city || ""}
                    onChange={e => handleFieldChange("city", e.target.value)}
                    className={classes.customInput}
                    placeholder="Enter city"
                  />
                </FormControl>
              </Box>
            )}

            {/* Bio Field */}
            <FormControl fullWidth style={{ marginTop: 40 }} className={classes.formControl}>
              <InputLabel shrink className={classes.customLabel}>
                Bio
              </InputLabel>
              <Input
                placeholder="Tell other users more about yourself"
                value={profileData.bio}
                className={classes.customInput}
                onChange={e => handleFieldChange("bio", e.target.value)}
                onBlur={e => handleFieldBlur("bio", e.target.value)}
                error={!!viewerErrors.bio}
              />
              {viewerErrors.bio && (
                <Typography
                  variant="caption"
                  style={{
                    color: "#f44336",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {viewerErrors.bio}
                </Typography>
              )}
            </FormControl>
          </Box>
        </div>

        <Box marginTop="60px" display="flex" justifyContent="space-between" width="100%">
          <Button
            variant="outlined"
            className={creatorClasses.button}
            onClick={prevStep}
            style={{
              backgroundColor: "transparent",
              border: "1px solid #FE5A97",
              color: "#FFFFFF",
            }}
          >
            Back
          </Button>

          <Box className={creatorClasses.buttonContainer}>
            <Button
              variant="contained"
              className={creatorClasses.button}
              disabled={!isFormValid || isSubmitting}
              onClick={handleProfileChange}
            >
              {isSubmitting ? (
                <CircularProgress size={24} style={{ color: "#FFFFFF" }} />
              ) : (
                "Next"
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default ViewerProfileStep;
